// src/App.js
import { useContext, useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  Link,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Container, Navbar, Nav, Badge } from "react-bootstrap";

import { AuthProvider, AuthContext } from "./AuthContext";
import { MessagesProvider, MessagesContext } from "./MessagesContext";
import { setUnauthorizedHandler } from "./api";
import { dashboardPathFor } from "./dashboardPath";

import LoginPage from "./pages/LoginPage";
import ClientListPage from "./pages/ClientListPage";
import ClientNewPage from "./pages/ClientNewPage";

import ClientEditPage from "./pages/ClientEditPage";
import EmploymentEditPage from "./pages/EmploymentEditPage";
import FinancialsEditPage from "./pages/FinancialsEditPage";
import HealthEditPage from "./pages/HealthEditPage";
import ClientIntakeSummaryPage from "./pages/ClientIntakeSummaryPage";
import DriverManagementPage from "./pages/DriverManagementPage";
import NavigationPage from "./pages/NavigationPage";
import AdminPage from "./pages/AdminPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminRolesPage from "./pages/AdminRolesPage";
import AdminServicesPage from "./pages/AdminServicesPage";
import AdminContactTypesPage from "./pages/AdminContactTypesPage";
import AdminMentorsPage from "./pages/AdminMentorsPage";
import AdminGasPropanePage from "./pages/AdminGasPropanePage";
import AdminBedsPage from "./pages/AdminBedsPage";
import AdminLaundryPage from "./pages/AdminLaundryPage";
import ForceChangePasswordPage from "./pages/ForceChangePasswordPage";
import TransportationDashboardPage from "./pages/TransportationDashboardPage";
import IntakeWorkerDashboardPage from "./pages/IntakeWorkerDashboardPage";
import DriverDashboardPage from "./pages/DriverDashboardPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import MentorDashboardPage from "./pages/MentorDashboardPage";
import ServicesReportPage from "./pages/ServicesReportPage";

import ClientDashboardPage from "./pages/ClientDashboardPage";
import ClientEditLayout from "./pages/ClientEditLayout";
import ClientViewLayout from "./pages/ClientViewLayout";
import ClientDemographicsViewPage from "./pages/ClientDemographicsViewPage";
import EmploymentViewPage from "./pages/EmploymentViewPage";
import FinancialsViewPage from "./pages/FinancialsViewPage";
import HealthViewPage from "./pages/HealthViewPage";
import IntakeSummaryViewPage from "./pages/IntakeSummaryViewPage";
import MessagesPage from "./pages/MessagesPage";
import TicketsPage from "./pages/TicketsPage";
import AddressBookModal from "./components/AddressBookModal";

import logo from "./assets/hhh-logo.png";

function DashboardRedirect() {
  const { id } = useParams();
  return <Navigate to={`/clients/${id}/view`} replace />;
}

function DefaultPage() {
  const { user } = useContext(AuthContext);
  return <Navigate to={dashboardPathFor(user)} replace />;
}

function PrivateRoute({ children }) {
  const { user, authReady } = useContext(AuthContext);
  const location = useLocation();

  if (!authReady) return <div>Loading…</div>;

  if (!user) {
    const next = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  return children;
}

// Mirrors the backend: DIRECTOR is treated as ADMIN everywhere.
function hasAnyRole(user, allowed) {
  const roles = new Set((user?.roles || []).map((r) => String(r).toUpperCase()));
  if (roles.has("DIRECTOR")) roles.add("ADMIN");
  return allowed.some((r) => roles.has(String(r).toUpperCase()));
}

// Defense-in-depth route guard. The backend independently enforces authz on
// every endpoint; this only stops a logged-in user from rendering a page they
// have no role for (and from issuing the calls behind it).
function RoleRoute({ roles, children }) {
  const { user, authReady } = useContext(AuthContext);
  const location = useLocation();

  if (!authReady) return <div>Loading…</div>;

  if (!user) {
    const next = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  if (!hasAnyRole(user, roles)) {
    return (
      <div className="text-center mt-5">
        <h4>Access denied</h4>
        <p className="text-muted">You do not have permission to view this page.</p>
        <Link to="/">Return to your dashboard</Link>
      </div>
    );
  }

  return children;
}


function Layout() {
  const { user, logout } = useContext(AuthContext);
  const { unread } = useContext(MessagesContext);
  const navigate = useNavigate();
  // Controlled collapse so the mobile hamburger menu closes after a selection.
  const [navExpanded, setNavExpanded] = useState(false);
  const collapseNav = () => setNavExpanded(false);
  const [showAddressBook, setShowAddressBook] = useState(false);

  // Where this user's dashboard lives. Single source of truth for the brand
  // logo, the first nav link, and the duplicate-link checks below.
  const dashPath = user ? dashboardPathFor(user) : "/";

  useEffect(() => {
    setUnauthorizedHandler(() => {
      // If we're already on login, don't bounce again.
      if (window.location.pathname.startsWith("/login")) return;

      const next = window.location.pathname + window.location.search;

      // Clear auth state (make sure your logout() also clears stored token + setAuthToken(null))
      logout();

      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
    });

    // optional cleanup
    return () => setUnauthorizedHandler(null);
  }, [logout, navigate]);

  return (
    <>
      <Navbar
        expand="lg"
        className="hhh-navbar"
        expanded={navExpanded}
        onToggle={setNavExpanded}
      >
        <Container fluid className="px-4">
          <Navbar.Brand as={Link} to={dashPath} className="hhh-brand" onClick={collapseNav}>
            <img src={logo} alt="His Helping Hands logo" className="hhh-logo me-2" />
            <span className="hhh-brand-text">His Helping Hands of Clare County</span>
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="hhh-navbar" />
          <Navbar.Collapse id="hhh-navbar">
            <Nav className="me-auto" onClick={collapseNav}>
              {/*
                Every user's own dashboard is always the first link, and it always
                agrees with dashboardPathFor (login redirect, "/" and the brand logo
                use the same helper). A user who holds DRIVER on top of a primary
                role lands on the primary dashboard, so they get "My Rides" as a
                second link rather than instead of the first one.
              */}
              {user && (
                <Nav.Link as={Link} to={dashPath}>
                  {dashPath === "/driver-dashboard" ? "My Rides" : "Dashboard"}
                </Nav.Link>
              )}
              {user && user.roles?.includes("DRIVER") && dashPath !== "/driver-dashboard" && (
                <Nav.Link as={Link} to="/driver-dashboard">
                  My Rides
                </Nav.Link>
              )}
              {user && (
                !user.roles?.includes("DRIVER") ||
                user.roles?.some((r) => ["ADMIN", "DIRECTOR", "INTAKE_WORKER", "NAVIGATOR"].includes(r))
              ) && (
                <Nav.Link as={Link} to="/clients">
                  Clients
                </Nav.Link>
              )}
              {user && hasAnyRole(user, ["ADMIN", "COORDINATOR", "INTAKE_WORKER"]) && (
                <Nav.Link onClick={() => setShowAddressBook(true)}>
                  Address Book
                </Nav.Link>
              )}
              {user && hasAnyRole(user, ["ADMIN", "DIRECTOR", "COORDINATOR", "INTAKE_WORKER"]) && (
                <Nav.Link as={Link} to="/transportation">
                  Transportation
                </Nav.Link>
              )}
              {user && (user.roles?.includes("ADMIN") || user.roles?.includes("DIRECTOR") || user.roles?.includes("COORDINATOR")) && (
                <Nav.Link as={Link} to="/drivers">
                  Drivers
                </Nav.Link>
              )}
              {user && (user.roles?.includes("ADMIN") || user.roles?.includes("DIRECTOR") || user.roles?.includes("NAVIGATOR")) && (
                <Nav.Link as={Link} to="/navigation">
                  Navigation
                </Nav.Link>
              )}
              {/* Skipped for mentor-only users: it would repeat their Dashboard link. */}
              {user && dashPath !== "/mentor-dashboard" &&
                (user.roles?.includes("ADMIN") || user.roles?.includes("DIRECTOR") || user.roles?.includes("MENTOR")) && (
                <Nav.Link as={Link} to="/mentor-dashboard">
                  Mentor
                </Nav.Link>
              )}
            </Nav>
            <Nav className="ms-auto align-items-center" onClick={collapseNav}>
              {user && (user.roles?.includes("ADMIN") || user.roles?.includes("DIRECTOR") || user.roles?.includes("NAVIGATOR")) && (
                <Nav.Link as={Link} to="/admin" title="Admin Settings" className="me-1 px-2">
                  <i className="bi bi-gear-fill" style={{ fontSize: "1.1rem" }} />
                </Nav.Link>
              )}
              {user && (
                <Nav.Link
                  as={Link}
                  to="/tickets"
                  title="Support & Feature Requests"
                  className="me-1 px-2"
                >
                  <i className="bi bi-life-preserver" style={{ fontSize: "1.1rem" }} />
                </Nav.Link>
              )}
              {user && (
                <Nav.Link
                  as={Link}
                  to="/messages"
                  title="Messages"
                  className="me-2 px-2 position-relative"
                >
                  <i className="bi bi-envelope-fill" style={{ fontSize: "1.1rem" }} />
                  {unread > 0 && (
                    <Badge
                      bg="danger"
                      pill
                      className="position-absolute top-0 start-100 translate-middle"
                      style={{ fontSize: "0.6rem" }}
                    >
                      {unread > 99 ? "99+" : unread}
                    </Badge>
                  )}
                </Nav.Link>
              )}
              {user ? (
                <>
                  <Navbar.Text className="me-3 hhh-navbar-text">
                    Signed in as: {user.full_name}
                  </Navbar.Text>
                  <Nav.Link onClick={logout}>Logout</Nav.Link>
                </>
              ) : (
                <Nav.Link as={Link} to="/login">
                  Login
                </Nav.Link>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <AddressBookModal show={showAddressBook} onHide={() => setShowAddressBook(false)} />

      <Container fluid className="mt-4">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/change-password" element={<ForceChangePasswordPage />} />

          <Route
            path="/messages"
            element={
              <PrivateRoute>
                <MessagesPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/tickets"
            element={
              <PrivateRoute>
                <TicketsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <IntakeWorkerDashboardPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/driver-dashboard"
            element={
              <RoleRoute roles={["ADMIN", "COORDINATOR", "DRIVER"]}>
                <DriverDashboardPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin-dashboard"
            element={
              <RoleRoute roles={["ADMIN"]}>
                <AdminDashboardPage />
              </RoleRoute>
            }
          />

          <Route
            path="/mentor-dashboard"
            element={
              <RoleRoute roles={["ADMIN", "NAVIGATOR", "MENTOR"]}>
                <MentorDashboardPage />
              </RoleRoute>
            }
          />

          <Route
            path="/reports/services"
            element={
              <RoleRoute roles={["ADMIN"]}>
                <ServicesReportPage />
              </RoleRoute>
            }
          />

          <Route
            path="/clients"
            element={
              <PrivateRoute>
                <ClientListPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/clients/new"
            element={
              <PrivateRoute>
                <ClientNewPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/transportation"
            element={
              <RoleRoute roles={["ADMIN", "COORDINATOR", "INTAKE_WORKER"]}>
                <TransportationDashboardPage />
              </RoleRoute>
            }
          />

          <Route
            path="/drivers"
            element={
              <RoleRoute roles={["ADMIN", "COORDINATOR"]}>
                <DriverManagementPage />
              </RoleRoute>
            }
          />

          <Route
            path="/navigation"
            element={
              <RoleRoute roles={["ADMIN", "NAVIGATOR"]}>
                <NavigationPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <RoleRoute roles={["ADMIN", "NAVIGATOR"]}>
                <AdminPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <RoleRoute roles={["ADMIN"]}>
                <AdminUsersPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/roles"
            element={
              <RoleRoute roles={["ADMIN"]}>
                <AdminRolesPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/services"
            element={
              <RoleRoute roles={["ADMIN"]}>
                <AdminServicesPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/mentors"
            element={
              <RoleRoute roles={["ADMIN", "NAVIGATOR"]}>
                <AdminMentorsPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/contact-types"
            element={
              <RoleRoute roles={["ADMIN"]}>
                <AdminContactTypesPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/gas-propane"
            element={
              <RoleRoute roles={["ADMIN"]}>
                <AdminGasPropanePage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/beds"
            element={
              <RoleRoute roles={["ADMIN"]}>
                <AdminBedsPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/laundry-ministry"
            element={
              <RoleRoute roles={["ADMIN"]}>
                <AdminLaundryPage />
              </RoleRoute>
            }
          />

          {/* Back-compat redirect: /clients/:id/dashboard -> /clients/:id/view */}
          <Route
            path="/clients/:id/dashboard"
            element={
              <PrivateRoute>
                <DashboardRedirect />
              </PrivateRoute>
            }
          />

          {/* -----------------------------
              EDIT TAB GROUP (/clients/:id/*)
              ----------------------------- */}
          <Route
            path="/clients/:id"
            element={
              <PrivateRoute>
                <ClientEditLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<ClientEditPage />} />
            <Route path="employment" element={<EmploymentEditPage />} />
            <Route path="financials" element={<FinancialsEditPage />} />
            <Route path="health" element={<HealthEditPage />} />
            <Route path="intake-summary" element={<ClientIntakeSummaryPage />} />
          </Route>

          {/* -----------------------------
              VIEW TAB GROUP (/clients/:id/view/*)
              ----------------------------- */}
          <Route
            path="/clients/:id/view"
            element={
              <PrivateRoute>
                <ClientViewLayout />
              </PrivateRoute>
            }
          >
            {/* Client Dashboard tab (default) */}
            <Route index element={<ClientDashboardPage />} />

            {/* Demographics tab */}
            <Route path="demographics" element={<ClientDemographicsViewPage />} />

            <Route path="employment" element={<EmploymentViewPage />} />
            <Route path="financials" element={<FinancialsViewPage />} />
            <Route path="health" element={<HealthViewPage />} />
            <Route path="intake-summary" element={<IntakeSummaryViewPage />} />
          </Route>

          <Route
            path="/"
            element={
              <PrivateRoute>
                <DefaultPage />
              </PrivateRoute>
            }
          />

          {/* catch-all */}
          <Route path="*" element={<PrivateRoute><DefaultPage /></PrivateRoute>} />
        </Routes>
      </Container>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <MessagesProvider>
        <Layout />
      </MessagesProvider>
    </AuthProvider>
  );
}